import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Key, Home, Copy, CheckCircle, Users, GraduationCap } from 'lucide-react';
import { useGame } from '../context/GameContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinkCodePage = () => {
  const navigate = useNavigate();
  const { playerName } = useGame();
  const [linkCode, setLinkCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!playerName) {
      navigate('/');
      return;
    }
    fetchLinkCode();
  }, [playerName, navigate]);

  const fetchLinkCode = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/player/${playerName}/link-code`);
      
      if (response.data.link_code) {
        setLinkCode(response.data.link_code);
        setHasActiveSubscription(true);
      } else {
        setHasActiveSubscription(false);
        setMessage(response.data.message || 'Link code not available');
      }
    } catch (error) {
      console.error('Error fetching link code:', error);
      setMessage('Failed to load link code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(linkCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
      }}>
        <Card className="p-12 bg-white/95 backdrop-blur-sm shadow-2xl text-center" style={{ borderRadius: '32px' }}>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Loading...</h2>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)'
    }}>
      <div className="max-w-3xl w-full">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-6 text-lg font-semibold text-white hover:scale-110 transition-transform"
        >
          <Home className="w-5 h-5 mr-2" />
          Home
        </Button>

        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl bounce-in" style={{
          borderRadius: '32px'
        }}>
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-400 to-orange-400 rounded-full flex items-center justify-center">
              <Key className="w-14 h-14 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Your Link Code</h1>
            <p className="text-xl text-gray-600">
              {hasActiveSubscription 
                ? 'Share this code with your parent or teacher'
                : 'Subscribe to get your link code'
              }
            </p>
          </div>

          {!hasActiveSubscription ? (
            <Card className="p-12 bg-gradient-to-br from-yellow-50 to-orange-50 border-4 border-yellow-300 text-center" style={{ borderRadius: '24px' }}>
              <div className="mb-6">
                <p className="text-2xl font-bold text-gray-800 mb-4">Subscription Required</p>
                <p className="text-lg text-gray-700 mb-6">
                  You need an active subscription to get your link code. Once you subscribe, your unique code will appear here!
                </p>
              </div>
              <Button
                onClick={() => navigate('/payment')}
                className="px-12 py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                  borderRadius: '16px'
                }}
              >
                Subscribe Now - $9.99/month
              </Button>
            </Card>
          ) : (
            <>
              <Card className="p-12 bg-gradient-to-br from-red-50 to-orange-50 border-4 border-red-200 mb-8" style={{ borderRadius: '24px' }}>
                <div className="text-center mb-6">
                  <p className="text-lg text-gray-600 mb-4">Hello, <span className="font-bold text-gray-800">{playerName}</span>!</p>
                  <div className="bg-white p-8 rounded-2xl shadow-lg mb-6">
                    <p className="text-7xl font-bold text-red-600 tracking-widest" style={{ fontFamily: 'monospace' }}>
                      {linkCode}
                    </p>
                  </div>
                  <Button
                    onClick={handleCopy}
                    className="px-8 py-4 text-lg font-bold transition-all duration-300 hover:scale-105"
                    style={{
                      background: copied ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                      borderRadius: '16px'
                    }}
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-5 h-5 mr-2" />
                        Copy Code
                      </>
                    )}
                  </Button>
                </div>
              </Card>

              <div className="space-y-6">
                <Card className="p-6 bg-blue-50 border-2 border-blue-200" style={{ borderRadius: '20px' }}>
                  <div className="flex items-start gap-4">
                    <Users className="w-8 h-8 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">For Parents</h3>
                      <p className="text-gray-700">
                        Give this code to your parent so they can link your account and track your learning progress!
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-green-50 border-2 border-green-200" style={{ borderRadius: '20px' }}>
                  <div className="flex items-start gap-4">
                    <GraduationCap className="w-8 h-8 text-green-600 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">For Teachers</h3>
                      <p className="text-gray-700">
                        Share this code with your teacher so they can monitor your classroom progress and help you learn!
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-yellow-50 border-2 border-yellow-300" style={{ borderRadius: '20px' }}>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">⚠️ Keep This Code Safe!</h3>
                  <ul className="list-disc list-inside text-gray-700 space-y-1">
                    <li>Only share with your parent or teacher</li>
                    <li>Don't share with strangers</li>
                    <li>You can always find this code here</li>
                  </ul>
                </Card>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LinkCodePage;
